import { useCallback, useEffect, useRef, useState } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import type { RootState } from 'store';
import { getAgencyIdByCountryCode } from 'features/auth/helpers';
import { CardDeskAccess, getCardDeskAccess } from '../helpers/access';
import {
	errorStatus,
	extractCardDeskError,
	isCardDeskNotEnabledError,
	isCardDeskNotFoundError,
} from '../helpers/errors';
import {
	CARD_DESK_LOG_MAX_LIMIT,
	ICardDeskEmissionDetail,
	getCardDeskAuthorizations,
	getCardDeskBins,
	getCardDeskBoard,
	getCardDeskCardDetail,
	getCardDeskCards,
	getCardDeskCharges,
	getCardDeskCrossability,
	getCardDeskCrossingPlatforms,
	getCardDeskEmissionDetail,
	getCardDeskEmissionNotes,
	getCardDeskEmissionPauses,
	getCardDeskRules,
	getCardDeskSettings,
} from '../services';
import {
	ICardDeskAuthorizationsData,
	ICardDeskAuthorizationsQuery,
	ICardDeskBinsData,
	ICardDeskBinsQuery,
	ICardDeskBoardData,
	ICardDeskBoardQuery,
	ICardDeskCardDetailData,
	ICardDeskCardsData,
	ICardDeskCardsQuery,
	ICardDeskChargesData,
	ICardDeskChargesQuery,
	ICardDeskCrossabilityData,
	ICardDeskCrossabilityQuery,
	ICardDeskCrossingPlatform,
	ICardDeskDetailQuery,
	ICardDeskEmissionNotesData,
	ICardDeskEmissionPausesData,
	ICardDeskRulesData,
	ICardDeskRulesQuery,
	ICardDeskSetting,
} from '../types';

interface QueryState<T> {
	data: T | null;
	loading: boolean;
	/** Mensaje legible; `null` si no hubo error o si la mesa no está habilitada. */
	error: string | null;
	/** La API respondió 404 o 503: la mesa no está habilitada en este ambiente. */
	notEnabled: boolean;
	/** La API respondió que lo pedido no está en la mesa (su 404, con su mensaje en `error`). */
	notFound: boolean;
	/** La API respondió 403: no le toca a su rol (el mensaje, en `error`). */
	forbidden: boolean;
}

/** Lo que devuelve cada hook de lectura de la mesa. */
export interface CardDeskQueryState<T> extends QueryState<T> {
	/** Vuelve a pedir. Mientras tanto se sigue mostrando lo último que llegó. */
	refetch: () => void;
}

/** Quién puede ver y quién puede cambiar, según el usuario logueado. */
export function useCardDeskAccess(): CardDeskAccess {
	const user = useSelector((state: RootState) => state.auth.user, shallowEqual);
	return getCardDeskAccess(user);
}

/** La agencia que usa la API cuando el pedido no dice cuál. */
export const CARD_DESK_DEFAULT_AGENCY_ID = 1;

/** El nombre de usuario de la sesión: decide si se ve la solapa "Mesa". */
export function useCardDeskUsername(): string | null {
	return useSelector((state: RootState) => state.auth.user?.username ?? null);
}

/** El nombre del usuario de la sesión: core lo pone en `createdBy` de los cruces que carga (`user.name`). */
export function useCardDeskSessionName(): string | null {
	return useSelector((state: RootState) => state.auth.user?.name ?? null);
}

/**
 * La agencia de la Mesa: siempre la 1. La mesa es una sola, la de la cartera de tarjetas de Uruguay (las reglas de
 * cruce y de tarjeta son de sus comercios), y muestra las emisiones de todas las BUs, cualquiera sea el país con el
 * que se entró al Power (pedido de emisiones, decidido por Sebastian el 2026-10-27). Antes seguía al selector de país,
 * y en PY/CO/BO/TN la mesa salía sin tarjetas ni reglas: todo cash.
 */
export function useCardDeskAgencyId(): number {
	return CARD_DESK_DEFAULT_AGENCY_ID;
}

/**
 * La agencia de la sesión (la del país con el que se entró al Power), como en el resto de las pantallas. `undefined`
 * si no se reconoce. La usan los cobros (el link de pago es del país del vendedor) y el aviso de la Mesa.
 */
export function useCardDeskSessionAgencyId(): number | undefined {
	const country = useSelector((state: RootState) => state.auth.country);
	const agencyId = country ? getAgencyIdByCountryCode(country) : 0;
	return agencyId > 0 ? agencyId : undefined;
}

/** El país de la sesión (`UY`, `PY`…): el `plCountryCode` del link de pago, como en el formulario de siempre. */
export function useCardDeskCountry(): string {
	return useSelector((state: RootState) => state.auth.country) ?? '';
}

/** `current` pasa a `false` al desmontar: una respuesta que llega tarde no toca el estado. */
export function useCardDeskAlive(): { readonly current: boolean } {
	const alive = useRef(true);
	useEffect(
		() => () => {
			alive.current = false;
		},
		[],
	);
	return alive;
}

/**
 * Lectura genérica. `key` identifica el pedido (los parámetros serializados):
 * si cambia, se vuelve a pedir. Una respuesta que llega después de desmontar,
 * o de que cambió el pedido, se descarta.
 *
 * Nunca deja escapar el error: un 404 o un 503 se marca como `notEnabled` y el
 * resto se traduce a un mensaje. Así la página no rompe ni desloguea. Cada
 * resultado se guarda de una sola vez: un render por respuesta.
 */
function useCardDeskQuery<T>(key: string, fetcher: () => Promise<T>, enabled: boolean): CardDeskQueryState<T> {
	const [state, setState] = useState<QueryState<T>>({
		data: null,
		loading: enabled,
		error: null,
		notEnabled: false,
		notFound: false,
		forbidden: false,
	});
	const [tick, setTick] = useState(0);
	// `fetcher` es una función nueva en cada render; lo que identifica el pedido
	// es `key`. Se guarda en una ref para usar siempre la última.
	const fetcherRef = useRef(fetcher);
	fetcherRef.current = fetcher;

	useEffect(() => {
		if (!enabled) {
			setState((s) => (s.loading ? { ...s, loading: false } : s));
			return;
		}
		let cancelled = false;
		setState((s) =>
			s.loading && !s.error && !s.notEnabled
				? s
				: { ...s, loading: true, error: null, notEnabled: false, notFound: false, forbidden: false },
		);
		fetcherRef.current().then(
			(data) => {
				if (!cancelled) {
					setState({ data, loading: false, error: null, notEnabled: false, notFound: false, forbidden: false });
				}
			},
			(err) => {
				if (cancelled) return;
				const notEnabled = isCardDeskNotEnabledError(err);
				setState({
					data: null,
					loading: false,
					error: notEnabled ? null : extractCardDeskError(err),
					notEnabled,
					notFound: isCardDeskNotFoundError(err),
					forbidden: errorStatus(err) === 403,
				});
			},
		);
		return () => {
			cancelled = true;
		};
	}, [key, enabled, tick]);

	const refetch = useCallback(() => setTick((t) => t + 1), []);
	return { ...state, refetch };
}

/** Tarjetas con los datos de su orden (`GET /cards`). */
export function useCardDeskCards(
	query: ICardDeskCardsQuery = {},
	enabled = true,
): CardDeskQueryState<ICardDeskCardsData> {
	return useCardDeskQuery(JSON.stringify(query), () => getCardDeskCards(query), enabled);
}

/** Reglas por aerolínea (`GET /rules`). */
export function useCardDeskRules(
	query: ICardDeskRulesQuery = {},
	enabled = true,
): CardDeskQueryState<ICardDeskRulesData> {
	return useCardDeskQuery(JSON.stringify(query), () => getCardDeskRules(query), enabled);
}

/** Los parámetros de la mesa (`GET /settings`). */
export function useCardDeskSettings(enabled = true): CardDeskQueryState<ICardDeskSetting[]> {
	return useCardDeskQuery('settings', getCardDeskSettings, enabled);
}

/** Por dónde se cruza (`GET /crossability`). */
export function useCardDeskCrossability(
	query: ICardDeskCrossabilityQuery = {},
	enabled = true,
): CardDeskQueryState<ICardDeskCrossabilityData> {
	return useCardDeskQuery(JSON.stringify(query), () => getCardDeskCrossability(query), enabled);
}

/** Las reglas aerolínea × plataforma de la agencia (`GET /crossing-platforms`). */
export function useCardDeskCrossingPlatforms(
	agencyId: number,
	enabled = true,
): CardDeskQueryState<ICardDeskCrossingPlatform[]> {
	return useCardDeskQuery(`crossing-platforms-${agencyId}`, () => getCardDeskCrossingPlatforms(agencyId), enabled);
}

/** El catálogo de BINs (`GET /bins`). */
export function useCardDeskBins(query: ICardDeskBinsQuery = {}, enabled = true): CardDeskQueryState<ICardDeskBinsData> {
	return useCardDeskQuery(JSON.stringify(query), () => getCardDeskBins(query), enabled);
}

/** Los cobros con link propio de la agencia (`GET /charges`). */
export function useCardDeskCharges(
	query: ICardDeskChargesQuery = {},
	enabled = true,
): CardDeskQueryState<ICardDeskChargesData> {
	return useCardDeskQuery(JSON.stringify(query), () => getCardDeskCharges(query), enabled);
}

/** El tablero de la mesa (`GET /board`). */
export function useCardDeskBoard(
	query: ICardDeskBoardQuery = {},
	enabled = true,
): CardDeskQueryState<ICardDeskBoardData> {
	return useCardDeskQuery(JSON.stringify(query), () => getCardDeskBoard(query), enabled);
}

/**
 * Una tarjeta de la mesa (`GET /cards/:transactionId`). `enabled`: su panel está
 * abierto. El 404 "no está en la mesa" llega como `error`, con el mensaje de la API.
 */
export function useCardDeskCardDetail(
	transactionId: number,
	query: ICardDeskDetailQuery = {},
	enabled = true,
): CardDeskQueryState<ICardDeskCardDetailData> {
	return useCardDeskQuery(
		JSON.stringify({ transactionId, ...query }),
		() => getCardDeskCardDetail(transactionId, query),
		enabled,
	);
}

/**
 * Una emisión de la mesa (`GET /emissions/:flightServiceId`). `enabled`: su panel
 * está abierto. Si ya no está en la mesa, `notFound` (y el mensaje de la API en `error`).
 */
export function useCardDeskEmissionDetail(
	flightServiceId: string,
	query: ICardDeskDetailQuery = {},
	enabled = true,
): CardDeskQueryState<ICardDeskEmissionDetail> {
	return useCardDeskQuery(
		JSON.stringify({ flightServiceId, ...query }),
		() => getCardDeskEmissionDetail(flightServiceId, query),
		enabled,
	);
}

/** El último hilo de notas leído de cada emisión (por agencia), con el `version` con el que se leyó. */
const notesRead = new Map<string, { version: unknown; data: ICardDeskEmissionNotesData }>();
const notesKey = (flightServiceId: string, agencyId: number | undefined) => `${agencyId ?? ''}|${flightServiceId}`;

/** Olvida el hilo leído de una emisión (se le agregó una nota); sin `flightServiceId`, todos. */
export function forgetCardDeskEmissionNotes(flightServiceId?: string, agencyId?: number): void {
	if (flightServiceId === undefined) notesRead.clear();
	else notesRead.delete(notesKey(flightServiceId, agencyId));
}

/**
 * Las notas de una emisión (`GET /emissions/:id/notes`, API-7d), hasta 500, de la más vieja a la más nueva. Se vuelven
 * a leer cuando cambia `version` (cuántas dice la mesa); sin `enabled` (ninguna), no se piden. Lo leído queda guardado:
 * la card que se vuelve a montar con el mismo `version` lo usa sin pedirlo.
 */
export function useCardDeskEmissionNotes(
	flightServiceId: string,
	query: ICardDeskDetailQuery,
	version: unknown,
	enabled = true,
): CardDeskQueryState<ICardDeskEmissionNotesData> {
	const full = { ...query, limit: CARD_DESK_LOG_MAX_LIMIT };
	const key = notesKey(flightServiceId, query.agencyId);
	const kept = notesRead.get(key);
	const cached = kept && kept.version === version ? kept.data : undefined;
	const read = useCardDeskQuery(
		JSON.stringify({ flightServiceId, ...full, version }),
		() =>
			getCardDeskEmissionNotes(flightServiceId, full).then((data) => {
				notesRead.set(key, { version, data });
				return data;
			}),
		enabled && !cached,
	);
	return cached ? { ...read, data: cached } : read;
}

/** El registro de pausas de una emisión (`GET /emissions/:id/pauses`, API-7d). Otra vez si cambia `version`. */
export function useCardDeskEmissionPauses(
	flightServiceId: string,
	query: ICardDeskDetailQuery,
	version: unknown,
): CardDeskQueryState<ICardDeskEmissionPausesData> {
	const full = { ...query, limit: CARD_DESK_LOG_MAX_LIMIT };
	return useCardDeskQuery(
		JSON.stringify({ flightServiceId, ...full, version }),
		() => getCardDeskEmissionPauses(flightServiceId, full),
		true,
	);
}

/** Las autorizaciones con saldo de una aerolínea, para reservar (`GET /authorizations`). */
export function useCardDeskAuthorizations(
	query: ICardDeskAuthorizationsQuery,
	enabled = true,
): CardDeskQueryState<ICardDeskAuthorizationsData> {
	return useCardDeskQuery(JSON.stringify(query), () => getCardDeskAuthorizations(query), enabled);
}
